import { useEffect, useState } from 'react';
import { Search, Send, Volume2, X } from 'lucide-react';
import { notificationService } from '../services/notificationService';
import { userService } from '../services/userService';

export function PushNotificationsAdmin() {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [title, setTitle] = useState('Yük Ortağım Test Bildirimi');
  const [message, setMessage] = useState('Test bildirimi başarıyla gönderildi.');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      const response = await userService.getUsers();
      setUsers(response.data || response || []);
    };
    loadUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const text = [user.full_name, user.username, user.email, user.phone, user.role]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return text.includes(userSearch.trim().toLowerCase());
  }).slice(0, 80);

  const selectedUsers = users.filter((user) => selectedUserIds.includes(String(user.id)));

  const toggleUser = (userId: string | number) => {
    const id = String(userId);
    setSelectedUserIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const handleSend = async () => {
    if (selectedUserIds.length === 0) {
      alert('Lütfen en az bir kullanıcı seçin');
      return;
    }

    try {
      setSending(true);
      const response = await notificationService.sendTestPush({
        userIds: selectedUserIds,
        title,
        message,
        channelId: soundEnabled ? 'weighbridge-alerts' : 'default',
        sound: soundEnabled ? 'default' : 'none',
      });
      const data = response.data || {};
      const apnsErrors = (data.apns?.responses || [])
        .filter((item: any) => !item.success)
        .map((item: any) => `${item.statusCode || '-'} ${item.error || ''}`.trim())
        .filter(Boolean);
      const fcmErrors = (data.fcm?.responses || [])
        .filter((item: any) => !item.success)
        .map((item: any) => item.error || item.statusCode || '')
        .filter(Boolean);
      const platformSummary = data.platformCounts
        ? Object.entries(data.platformCounts).map(([platform, count]) => `${platform}: ${count}`).join(', ')
        : '';
      const detailLines = [
        response.message || 'Test push gönderildi',
        `Kullanıcı: ${data.userCount || selectedUserIds.length}`,
        `Token: ${data.tokenCount || 0}`,
        platformSummary ? `Platform: ${platformSummary}` : '',
        `Toplam başarılı: ${data.successCount || 0}`,
        `Toplam hatalı: ${data.failureCount || 0}`,
        data.fcm ? `FCM başarılı/hatalı: ${data.fcm.successCount || 0}/${data.fcm.failureCount || 0}` : '',
        fcmErrors.length ? `FCM hata: ${fcmErrors.join(' | ')}` : '',
        data.apns ? `APNs başarılı/hatalı: ${data.apns.successCount || 0}/${data.apns.failureCount || 0}` : '',
        apnsErrors.length ? `APNs hata: ${apnsErrors.join(' | ')}` : '',
      ].filter(Boolean);
      alert(detailLines.join('\n'));
    } catch (error: any) {
      console.error('Send test push failed:', error);
      alert(error?.message || 'Test push gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Push Bildirim Testi</h1>
        <p className="text-sm text-gray-600 mt-1">Kayıtlı push token’ı olan bir veya birden fazla kullanıcıya sesli test bildirimi gönderin</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-3xl space-y-4">
        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <label className="block text-sm font-medium text-gray-700">Kullanıcılar</label>
            <span className="text-xs text-gray-500">{selectedUserIds.length} kullanıcı seçildi</span>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Ad, kullanıcı adı, e-posta, telefon veya rol ile ara"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2"
            />
          </div>
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleUser(user.id)}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                >
                  {user.full_name || user.username || `Kullanıcı #${user.id}`}
                  <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
          <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
            {filteredUsers.map((user) => {
              const checked = selectedUserIds.includes(String(user.id));
              return (
                <label key={user.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(user.id)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">{user.full_name || user.username || `Kullanıcı #${user.id}`}</span>
                    <span className="block text-xs text-gray-500 truncate">{user.username || '-'}{user.role ? ` • ${user.role}` : ''}</span>
                  </span>
                </label>
              );
            })}
            {filteredUsers.length === 0 && <div className="px-3 py-6 text-sm text-gray-500 text-center">Kullanıcı bulunamadı</div>}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none" />
        </div>
        <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 cursor-pointer">
          <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
          <Volume2 className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-gray-700">Sesli bildirim gönder</span>
        </label>
        <button onClick={handleSend} disabled={sending} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">
          <Send className="w-4 h-4" /> {sending ? 'Gönderiliyor...' : 'Test Bildirimi Gönder'}
        </button>
      </div>
    </div>
  );
}
